import { TRIP_STATE } from '@allocab/allocab-commons-shared-types';
import { TripOutput } from '@allocab/allocab-passenger-shared-types';
import { getPanelNameLabelByKey } from '@allocab/allocab-shared-utils';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, Outlet, useSearchParams } from 'react-router-dom';

import {
  DEPARTURE_AT_FILTER_FORMAT,
  DEPARTURE_DAY_SEARCH_PARAMS_KEY,
} from './_authenticated.trips/TripDepartureAtFilter';
import { TripsFilters } from './_authenticated.trips/TripsFilters';
import { STATE_SEARCH_PARAMS_KEY } from './_authenticated.trips/TripStateFilter';
import { BadgeIndicator } from '../components/BadgeIndicator';
import { Table } from '../components/table/Table';
import { TableColumn } from '../components/table/types';
import { LIMIT, useFetchTripsPage } from '../hooks/useFetchTrips';
import { useFetchUser } from '../hooks/useFetchUser';
import { queryKeys } from '../services/datastore/queryKeys';
import { queryClient } from '../services/datastore/react-query';
import { t } from '../services/translation/translation';
import { getFullName } from '../utils/common';
import { formatDateForParisTZ } from '../utils/date';
import { formatPrice, getStepForTrip, getTripStatus } from '../utils/trip';

const columnsAdmin: TableColumn<TripOutput>[] = [
  {
    name: t('trips-route.commission'),
    render: trip =>
      trip.resume.customerRemuneration ? (
        <p className="font-bold">
          {formatPrice(
            trip.state === 'CANCELLED' ? 0 : trip.resume.customerRemuneration.amount,
            trip.resume.customerRemuneration.currency,
            2,
            2,
          )}
          <span className="ml-1 text-[10px] opacity-50 font-normal">{t('common.ht')}</span>
        </p>
      ) : (
        '--'
      ),
  },
];

const columns: TableColumn<TripOutput>[] = [
  {
    name: t('trips-route.id'),
    render: trip => {
      const step = getStepForTrip(trip);

      return (
        <div className="flex flex-col items-start">
          <Link
            to={`/trips/${trip.key}`}
            onClick={() => queryClient.setQueryData(queryKeys.user.fetchTrip(trip.key), trip)}
          >
            <span className="font-bold hover:underline">R{trip.id}</span>
          </Link>
          <BadgeIndicator variant={step === 'DONE' ? 'green' : step === 'CANCELLED' ? 'red' : 'yellow'}>
            {getTripStatus(trip)}
          </BadgeIndicator>
        </div>
      );
    },
  },
  {
    name: t('common.departure-at'),
    render: trip => <p className="w-36">{formatDateForParisTZ(trip.resume.departureAt, t('date.datetime'))}</p>,
    sortKey: 'resume.departureAt',
  },
  { name: t('common.passenger'), render: trip => getFullName(trip.passenger.firstName, trip.passenger.lastName) },
  { name: t('common.panel'), render: trip => getPanelNameLabelByKey(trip.panelVars.key) },
  { name: t('trips-route.invoice-mention'), render: trip => trip.tripVars.invoiceMention ?? 'Client' },
  {
    name: t('trips-route.passenger-price'),
    render: trip => (
      <p className="font-bold">
        {formatPrice(trip.resume.price.amountWithTax, trip.resume.price.currency, 2, 2)}
        <span className="ml-1 text-[10px] opacity-50 font-normal">{t('common.ttc')}</span>
      </p>
    ),
  },
];

export const TripsRoute = () => {
  const [searchParams] = useSearchParams();
  const currentPage = Math.max(parseInt(searchParams.get('page') ?? '1'), 1);
  const orderBy = searchParams.get('orderBy') ?? 'resume.departureAt';
  const direction = (searchParams.get('direction') ?? 'desc') as 'desc' | 'asc';
  const state = (searchParams.get(STATE_SEARCH_PARAMS_KEY)?.split(',') ?? undefined) as TRIP_STATE[] | undefined;
  const departureDay = searchParams.get(DEPARTURE_DAY_SEARCH_PARAMS_KEY);
  const { data: user } = useFetchUser();
  const { data, isFetching, isLoading, isPreviousData } = useFetchTripsPage(
    {
      ...(departureDay
        ? {
            departureAt: {
              start: dayjs.tz(departureDay, DEPARTURE_AT_FILTER_FORMAT, 'Europe/Paris').startOf('day').valueOf(),
              end: dayjs.tz(departureDay, DEPARTURE_AT_FILTER_FORMAT, 'Europe/Paris').endOf('day').valueOf(),
            },
          }
        : {}),
      state,
      orderBy,
      direction,
    },
    currentPage,
    { keepPreviousData: true },
  );
  const nbPages = Math.ceil((data?.totalItems ?? 0) / LIMIT);
  const filteredColumns = useMemo(() => [...columns, ...(user?.admin ? columnsAdmin : [])], [user?.admin]);

  return (
    <div className="p-4">
      <Helmet>
        <title>{t('trips-route.meta-title')}</title>
      </Helmet>
      <h1 className="text-2xl mb-4">{t('trips-route.title')}</h1>
      <TripsFilters />
      <Table
        columns={filteredColumns}
        data={data?.items}
        isLoading={isLoading}
        isFetching={isPreviousData && isFetching}
        nbPages={nbPages}
        context={undefined}
        totalItems={data?.totalItems ?? 0}
      />
      <Outlet />
    </div>
  );
};
